import type { Substance } from './types';

function normalize(s: string): string {
  return s.trim().toLowerCase();
}

/** Classic edit-distance, used to tolerate small typos in the substance search. */
export function levenshteinDistance(a: string, b: string): number {
  const rows = a.length + 1;
  const cols = b.length + 1;
  const matrix: number[][] = Array.from({ length: rows }, () => new Array(cols).fill(0));

  for (let i = 0; i < rows; i++) matrix[i][0] = i;
  for (let j = 0; j < cols; j++) matrix[0][j] = j;

  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      );
    }
  }

  return matrix[rows - 1][cols - 1];
}

/**
 * Finds the best substance match for free-text input: exact alias match,
 * then substring containment, then typo-tolerant fuzzy match (edit distance
 * scaled to the alias length). Returns null if nothing matches well enough
 * to avoid false positives between genuinely distinct substances.
 */
export function findSubstanceMatch(query: string, substances: Substance[]): Substance | null {
  const q = normalize(query);
  if (!q) return null;

  for (const substance of substances) {
    if (substance.aliases.some((alias) => normalize(alias) === q)) return substance;
  }

  for (const substance of substances) {
    if (substance.aliases.some((alias) => normalize(alias).includes(q) || q.includes(normalize(alias)))) {
      return substance;
    }
  }

  let bestSubstance: Substance | null = null;
  let bestDistance = Infinity;

  for (const substance of substances) {
    for (const alias of substance.aliases) {
      const normalizedAlias = normalize(alias);
      const distance = levenshteinDistance(q, normalizedAlias);
      const threshold = Math.max(1, Math.floor(normalizedAlias.length * 0.3));
      if (distance <= threshold && distance < bestDistance) {
        bestDistance = distance;
        bestSubstance = substance;
      }
    }
  }

  return bestSubstance;
}
